import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { validateSceneDesignId } from './scene-design-validation';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { addSceneWithDesignOperation, saveRawLuminary } from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const legacyAddSceneSchema = z.object({
    scene_index: z.number().describe('The index where the new scene should be inserted (0-based)'),
    scene_design_id: z
        .string()
        .describe('The scene design ID to use for the new scene (e.g., "template_id:design_name")'),
});
export type AddSceneArgs = z.infer<typeof legacyAddSceneSchema>;

export class LegacyAddSceneTool extends VideoStateTool<typeof legacyAddSceneSchema> {
    protected override readonly schema = legacyAddSceneSchema;
    protected override readonly toolName = TOOL_NAMES.addScene;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription =
        'Add a new scene to the video at the specified index, using the specified scene design.';

    public override getToolDisplayName(): string {
        return 'Adding scene';
    }

    protected override onEditApplied(args: AddSceneArgs): void {
        // Adding a scene at index 0 changes the first scene shown in the thumbnail.
        if (args.scene_index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'add_photo_alternate';
    }

    public async execute(args: AddSceneArgs, context: ToolExecutionContext) {
        const { scene_index, scene_design_id } = args;
        const { parentActionGroup } = context;

        try {
            if (scene_index < 0 || scene_index > this.luminary.sceneGroups.length) {
                return `Error: scene_index ${scene_index} is out of bounds. Must be between 0 and ${this.luminary.sceneGroups.length}`;
            }
            if (!scene_design_id) {
                return 'Error: scene_design_id is required (e.g., "template_id:design_name")';
            }

            const validation = validateSceneDesignId(this.luminary, scene_design_id);
            if (validation.error) {
                return validation.error;
            }

            try {
                const { addSceneWithDesign } = makeOperationsCallable(
                    {
                        addSceneWithDesignOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this.getState as () => RootState,
                );

                const result = addSceneWithDesign(scene_index, scene_design_id);

                // Mark the new scene as "building" so the build preview blurs it
                // until the content it needs lands (set_text/set_media/image_ask).
                this.startSceneBuilding(result.sceneId);

                // Save changes
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                return `Success: Added new scene at index ${scene_index} using scene design "${result.sceneDesignId}"`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error adding scene: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding scene: ${errorMessage}`;
        }
    }
}
